import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/config/api";
import { ArrowLeft, Building2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { showError } from "@/utils/dialog";
import { addDaysToLocalDateInputValue, toLocalDateInputValue } from "@/utils/date";

const VENDOR_CATEGORIES = [
  { id: "hotel", label: "Hotel" },
  { id: "travel_agency", label: "Travel Agency" },
  { id: "insurance", label: "Insurance" },
  { id: "car_rental", label: "Car Rental" },
  { id: "visa_agent", label: "Visa Agent" },
  { id: "tour_operator", label: "Tour Operator" },
  { id: "other", label: "Other" },
];

export default function ViewVendor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canAdjust = ["admin", "finance"].includes(
    String(user?.role || "").toLowerCase(),
  );
  const [vendor, setVendor] = useState(null);
  const [payableBalance, setPayableBalance] = useState(null);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [ledgerPayableBalance, setLedgerPayableBalance] = useState(0);
  const [settlement, setSettlement] = useState(null);
  const [refundAllocations, setRefundAllocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postingAdjustment, setPostingAdjustment] = useState(false);
  const [adjustment, setAdjustment] = useState({
    action: "add",
    amount: "",
    adjustment_date: toLocalDateInputValue(),
    notes: "",
  });

  useEffect(() => {
    if (!id) return;
    Promise.all([
      api.vendors.getById(id),
      api.vendors.getAll(),
      apiRequestSafe(() => api.payments.getVendorSettlement(id)),
      apiRequestSafe(() => api.payments.getRefundAllocations({ vendor_id: id })),
    ])
      .then(([v, all, settlementData, allocationRows]) => {
        setVendor(v);
        const row = (all || []).find((x) => x.id === id);
        setPayableBalance(
          settlementData?.net_balance != null
            ? settlementData.net_balance
            : row?.net_balance != null
              ? row.net_balance
              : row?.payable_balance != null
                ? row.payable_balance
                : null,
        );
        setOpeningBalance(Number(settlementData?.opening_balance || row?.opening_balance || 0));
        setLedgerPayableBalance(
          Number(
            settlementData?.ledger_payable_balance != null
              ? settlementData.ledger_payable_balance
            : row?.payable_balance != null
                ? row.payable_balance
                : 0,
          ),
        );
        setSettlement(settlementData || null);
        setRefundAllocations(Array.isArray(allocationRows) ? allocationRows : []);
      })
      .catch(() => navigate("/vendors"))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  function apiRequestSafe(fn) {
    return fn().catch(() => null);
  }

  const settlementTotals = useMemo(
    () =>
      (settlement?.settlements || []).reduce(
        (sum, row) => sum + Number(row.amount || 0),
        0,
      ),
    [settlement],
  );
  const refundTotals = useMemo(
    () =>
      (refundAllocations || []).reduce(
        (acc, row) => {
          acc.sell += Number(row.refund_sell_amount || 0);
          acc.cost += Number(row.refund_cost_amount || 0);
          acc.cash += Number(row.vendor_cash_received_amount || 0);
          return acc;
        },
        { sell: 0, cost: 0, cash: 0 },
      ),
    [refundAllocations],
  );

  if (loading || !vendor) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const categoryLabel = (id) =>
    VENDOR_CATEGORIES.find((c) => c.id === id)?.label || id;
  const categoryItems =
    Array.isArray(vendor.categories) && vendor.categories.length
      ? vendor.categories
      : vendor.category
        ? String(vendor.category)
            .split(",")
            .map((c) => c.trim())
            .filter(Boolean)
        : [];

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => navigate("/vendors")}
          className="p-2 hover:bg-stone-100 rounded-lg"
        >
          <ArrowLeft size={20} className="text-stone-600" />
        </button>
        <Building2 className="text-secondary" size={28} />
        <div>
          <h1 className="text-2xl font-heading font-semibold text-secondary">
            {vendor.name}
          </h1>
          <div className="mt-1 flex flex-wrap gap-1">
            {categoryItems.length ? (
              categoryItems.map((categoryId) => (
                <span
                  key={`vendor-category-${categoryId}`}
                  className="inline-flex items-center rounded-full border border-stone-200 bg-stone-50 px-2 py-0.5 text-xs text-stone-700"
                >
                  {categoryLabel(categoryId)}
                </span>
              ))
            ) : (
              <span className="text-sm text-stone-500">Vendor</span>
            )}
          </div>
        </div>
        {!canAdjust && (
          <button
            type="button"
            onClick={() =>
              navigate(
                `/workflow-tickets?source_type=other&source_id=${encodeURIComponent(id)}&to_role=finance&title=Vendor%20account%20update%20required&description=Please%20review%20vendor%20ledger%20or%20settlement%20for%20this%20vendor.&due_date=${addDaysToLocalDateInputValue(1)}`,
              )
            }
            className="ml-auto border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 text-sm rounded-lg px-3 py-2"
          >
            Generate Finance Ticket
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-stone-300 p-4 shadow-sm">
          <p className="text-xs text-stone-500">Net payable balance</p>
          <p className="text-xl font-semibold text-stone-900">
            {payableBalance != null
              ? Number(payableBalance).toFixed(2)
              : "—"}
          </p>
          <p className="mt-1 text-[11px] text-stone-500">
            Opening {openingBalance.toFixed(2)} + Ledger {ledgerPayableBalance.toFixed(2)}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-stone-300 p-4 shadow-sm">
          <p className="text-xs text-stone-500">Contact</p>
          <p className="text-stone-800">{vendor.contact_name || "—"}</p>
          <p className="text-sm text-stone-600">{vendor.email || "—"}</p>
          <p className="text-sm text-stone-600">{vendor.phone || "—"}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-stone-300 p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-stone-800 mb-2">Address</h2>
        <p className="text-stone-700 whitespace-pre-wrap">
          {vendor.address || "—"}
        </p>
      </div>

      {vendor.notes && (
        <div className="bg-white rounded-xl border border-stone-300 p-4 shadow-sm">
          <h2 className="text-sm font-semibold text-stone-800 mb-2">Notes</h2>
          <p className="text-stone-700 whitespace-pre-wrap">{vendor.notes}</p>
        </div>
      )}

      {canAdjust && (
        <div className="bg-white rounded-xl border border-stone-300 shadow-sm p-4 space-y-3">
          <h3 className="font-semibold text-stone-800">Vendor balance adjustment</h3>
          <p className="text-xs text-stone-500">
            Use Add/Withdraw for payable adjustments with full ledger history.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <div>
              <label className="block text-xs text-stone-600 mb-1">Action</label>
              <select
                value={adjustment.action}
                onChange={(e) =>
                  setAdjustment((prev) => ({ ...prev, action: e.target.value }))
                }
                className="w-full border border-stone-300 rounded-lg px-3 py-2"
              >
                <option value="add">Add</option>
                <option value="withdraw">Withdraw</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-stone-600 mb-1">Amount</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={adjustment.amount}
                onChange={(e) =>
                  setAdjustment((prev) => ({ ...prev, amount: e.target.value }))
                }
                className="w-full border border-stone-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-xs text-stone-600 mb-1">Date</label>
              <input
                type="date"
                value={adjustment.adjustment_date}
                onChange={(e) =>
                  setAdjustment((prev) => ({
                    ...prev,
                    adjustment_date: e.target.value,
                  }))
                }
                className="w-full border border-stone-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-xs text-stone-600 mb-1">Notes</label>
              <input
                type="text"
                value={adjustment.notes}
                onChange={(e) =>
                  setAdjustment((prev) => ({ ...prev, notes: e.target.value }))
                }
                className="w-full border border-stone-300 rounded-lg px-3 py-2"
              />
            </div>
            <button
              type="button"
              disabled={postingAdjustment}
              onClick={async () => {
                setPostingAdjustment(true);
                try {
                  await api.accounting.createBalanceAdjustment({
                    party_type: "vendor",
                    party_id: id,
                    action: adjustment.action,
                    amount: Number(adjustment.amount || 0),
                    adjustment_date: adjustment.adjustment_date,
                    notes: adjustment.notes,
                  });
                  setAdjustment((prev) => ({ ...prev, amount: "", notes: "" }));
                  const [all, settlementData, allocationRows] = await Promise.all([
                    api.vendors.getAll(),
                    api.payments.getVendorSettlement(id),
                    api.payments.getRefundAllocations({ vendor_id: id }).catch(() => []),
                  ]);
                  const row = (all || []).find((x) => x.id === id);
                  setPayableBalance(
                    settlementData?.net_balance != null
                      ? settlementData.net_balance
                      : row?.net_balance != null
                        ? row.net_balance
                        : row?.payable_balance != null
                          ? row.payable_balance
                          : null,
                  );
                  setOpeningBalance(
                    Number(settlementData?.opening_balance || row?.opening_balance || 0),
                  );
                  setLedgerPayableBalance(
                    Number(
                      settlementData?.ledger_payable_balance != null
                        ? settlementData.ledger_payable_balance
                        : row?.ledger_payable != null
                          ? row.ledger_payable
                          : 0,
                    ),
                  );
                  setSettlement(settlementData || null);
                  setRefundAllocations(Array.isArray(allocationRows) ? allocationRows : []);
                } catch (e) {
                  await showError(e.message || "Failed to post adjustment");
                } finally {
                  setPostingAdjustment(false);
                }
              }}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm disabled:opacity-50"
            >
              {postingAdjustment ? "Posting..." : "Post adjustment"}
            </button>
          </div>
        </div>
      )}

      {settlement && (
        <div className="bg-white rounded-xl border border-stone-300 shadow-sm overflow-hidden">
          <div className="bg-black text-white px-4 py-2 font-bold tracking-wide">
            VENDOR SETTLEMENT
          </div>
          <div className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div className="rounded-lg bg-stone-50 p-3">
              <p className="text-xs text-stone-500">Amount to pay</p>
              <p className="font-semibold">{Number(settlement.amount_to_pay || 0).toFixed(3)}</p>
            </div>
            <div className="rounded-lg bg-stone-50 p-3">
              <p className="text-xs text-stone-500">Credit balance</p>
              <p className="font-semibold">{Number(settlement.credit_balance || 0).toFixed(3)}</p>
            </div>
            <div className="rounded-lg bg-stone-50 p-3">
              <p className="text-xs text-stone-500">Net balance</p>
              <p className="font-semibold">{Number(settlement.net_balance || 0).toFixed(3)}</p>
            </div>
          </div>
          {settlement.aging_summary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <div className="rounded-lg bg-stone-50 p-3">
                <p className="text-xs text-stone-500">0-30 days</p>
                <p className="font-semibold">{Number(settlement.aging_summary.current_0_30 || 0).toFixed(3)}</p>
              </div>
              <div className="rounded-lg bg-stone-50 p-3">
                <p className="text-xs text-stone-500">31-60 days</p>
                <p className="font-semibold">{Number(settlement.aging_summary.overdue_31_60 || 0).toFixed(3)}</p>
              </div>
              <div className="rounded-lg bg-stone-50 p-3">
                <p className="text-xs text-stone-500">61-90 days</p>
                <p className="font-semibold">{Number(settlement.aging_summary.overdue_61_90 || 0).toFixed(3)}</p>
              </div>
              <div className="rounded-lg bg-stone-50 p-3">
                <p className="text-xs text-stone-500">90+ days</p>
                <p className="font-semibold">{Number(settlement.aging_summary.overdue_90_plus || 0).toFixed(3)}</p>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-amber-200">
                <tr>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-right px-3 py-2">Amount</th>
                  <th className="text-left px-3 py-2">Reference</th>
                </tr>
              </thead>
              <tbody>
                {(settlement.settlements || []).map((row) => (
                  <tr key={row.id} className="border-t border-stone-100">
                    <td className="px-3 py-2">{String(row.payment_date || "").slice(0, 10)}</td>
                    <td className="px-3 py-2 text-right">{Number(row.amount || 0).toFixed(3)}</td>
                    <td className="px-3 py-2">{row.reference_number || "—"}</td>
                  </tr>
                ))}
              </tbody>
              {(settlement.settlements || []).length > 0 && (
                <tfoot>
                  <tr className="bg-black text-white font-semibold">
                    <td className="px-3 py-2">TOTAL</td>
                    <td className="px-3 py-2 text-right font-mono">
                      {settlementTotals.toFixed(3)}
                    </td>
                    <td className="px-3 py-2" />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          </div>
        </div>
      )}

      {settlement && Array.isArray(settlement.ledger_entries) && settlement.ledger_entries.length > 0 && (
        <div className="bg-white rounded-xl border border-stone-300 shadow-sm overflow-hidden">
          <div className="bg-stone-800 text-white px-4 py-2 font-bold tracking-wide">
            VENDOR LEDGER
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-100">
                <tr>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Account</th>
                  <th className="text-right px-3 py-2">Amount</th>
                  <th className="text-left px-3 py-2">Description</th>
                </tr>
              </thead>
              <tbody>
                {settlement.ledger_entries.map((row) => (
                  <tr key={row.id} className="border-t border-stone-100">
                    <td className="px-3 py-2">{String(row.entry_date || "").slice(0, 10)}</td>
                    <td className="px-3 py-2">{row.entry_type || "—"}</td>
                    <td className="px-3 py-2">{row.account_type || "—"}</td>
                    <td
                      className={`px-3 py-2 text-right font-mono ${
                        Number(row.amount || 0) < 0
                          ? "text-emerald-700"
                          : Number(row.amount || 0) > 0
                            ? "text-amber-700"
                            : "text-stone-700"
                      }`}
                    >
                      {Number(row.amount || 0).toFixed(3)} {row.currency || "OMR"}
                    </td>
                    <td className="px-3 py-2">{row.description || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {refundAllocations.length > 0 && (
        <div className="bg-white rounded-xl border border-stone-300 shadow-sm overflow-hidden">
          <div className="bg-amber-200 px-4 py-2 font-bold text-stone-900 tracking-wide">
            REFUND ALLOCATION DETAILS
          </div>
          <div className="px-4 pt-3 text-xs text-stone-500">
            Refund lines where this vendor was reversed, including optional immediate cash return.
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-amber-200">
                <tr>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Invoice</th>
                  <th className="text-left px-3 py-2">Service</th>
                  <th className="text-right px-3 py-2">Sell Reversed</th>
                  <th className="text-right px-3 py-2">Cost Reversed</th>
                  <th className="text-right px-3 py-2">Cash Received</th>
                </tr>
              </thead>
              <tbody>
                {refundAllocations.map((row) => (
                  <tr key={row.id} className="border-t border-stone-100">
                    <td className="px-3 py-2">{String(row.refund_date || "").slice(0, 10)}</td>
                    <td className="px-3 py-2">{row.invoice_number || "—"}</td>
                    <td className="px-3 py-2">{row.service_name || "—"}</td>
                    <td className="px-3 py-2 text-right">{Number(row.refund_sell_amount || 0).toFixed(3)}</td>
                    <td className="px-3 py-2 text-right">{Number(row.refund_cost_amount || 0).toFixed(3)}</td>
                    <td className="px-3 py-2 text-right">{Number(row.vendor_cash_received_amount || 0).toFixed(3)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-black text-white font-semibold">
                  <td className="px-3 py-2" colSpan={3}>TOTAL</td>
                  <td className="px-3 py-2 text-right font-mono">{refundTotals.sell.toFixed(3)}</td>
                  <td className="px-3 py-2 text-right font-mono">{refundTotals.cost.toFixed(3)}</td>
                  <td className="px-3 py-2 text-right font-mono">{refundTotals.cash.toFixed(3)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
